import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../utils/api'
import Button from '../components/ui/Button'
import { ChevronRight, Search, CheckCircle, EyeOff } from 'lucide-react'
import { usePublicSettings } from '../hooks/usePublicSettings'

const STATUS_LABELS = {
  draft:            'مسودة',
  submitted:        'مقدَّم',
  'pending review': 'قيد المراجعة',
  documented:       'موثَّق',
  rejected:         'مرفوض',
}
const STATUS_BADGE = {
  draft:            'bg-stone-100 text-stone-600',
  submitted:        'bg-blue-100 text-blue-700',
  'pending review': 'bg-amber-100 text-amber-700',
  documented:       'bg-green-100 text-green-700',
  rejected:         'bg-red-100 text-red-700',
}

const FIELD_LABELS = {
  phone_primary:          'رقم الهاتف الأساسي',
  phone_secondary:        'رقم الهاتف الثانوي',
  contact_primary_phone:  'هاتف جهة التواصل',
  contact_secondary_phone:'هاتف التواصل الثانوي',
  contact_whatsapp:       'رقم الواتساب',
  current_residence:      'آخر مكان إقامة',
  submitter_notes:        'ملاحظات إضافية',
}

export default function TrackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { settings, loading: settingsLoading } = usePublicSettings()

  const [refNumber, setRefNumber] = useState(() => searchParams.get('ref') || '')
  const [phone,     setPhone]     = useState('')
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState('')

  const [caseInfo,        setCaseInfo]       = useState(null)
  const [editValues,      setEditValues]     = useState({})
  const [updateLoading,   setUpdateLoading]  = useState(false)
  const [updateSuccess,   setUpdateSuccess]  = useState(false)

  async function handleLookup(e) {
    e.preventDefault()
    setError('')
    setCaseInfo(null)
    setUpdateSuccess(false)
    if (!refNumber.trim() || !phone.trim()) {
      setError('يرجى إدخال الرقم المرجعي ورقم الهاتف')
      return
    }
    setLoading(true)
    try {
      const { data } = await api.get(
        `submissions/lookup/${refNumber.trim().toUpperCase()}?phone=${encodeURIComponent(phone.trim())}`
      )
      setCaseInfo(data)
      // Pre-fill edit fields with current values
      const initial = {}
      for (const f of data.editable_fields || []) {
        initial[f] = data[f] || ''
      }
      setEditValues(initial)
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'حدث خطأ')
    } finally {
      setLoading(false)
    }
  }

  async function handleUpdate(e) {
    e.preventDefault()
    setError('')
    setUpdateSuccess(false)
    setUpdateLoading(true)
    try {
      await api.patch(
        `submissions/update/${caseInfo.readable_id}`,
        { phone: phone.trim(), ...editValues }
      )
      setUpdateSuccess(true)
      // Re-fetch to show latest
      const { data } = await api.get(
        `submissions/lookup/${caseInfo.readable_id}?phone=${encodeURIComponent(phone.trim())}`
      )
      setCaseInfo(data)
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'فشل التحديث')
    } finally {
      setUpdateLoading(false)
    }
  }

  if (settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-stone-400 text-lg">يتحمّل...</div>
      </div>
    )
  }

  if (!settings.tracking_enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4" dir="rtl">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-10 text-center">
          <div className="flex justify-center mb-5">
            <div className="bg-stone-100 p-5 rounded-full">
              <EyeOff className="w-10 h-10 text-stone-400" />
            </div>
          </div>
          <h1 className="text-xl font-bold text-stone-800 mb-3">صفحة المتابعة غير متاحة</h1>
          <p className="text-stone-500">خاصية متابعة الطلبات مغلقة حالياً.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface" dir="rtl">
      {/* Header */}
      <header className="bg-primary-700 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <img src="/logos/logo_icon.png" alt="" className="h-10 w-auto" />
          <div>
            <h1 className="text-base font-bold leading-tight">
              المنصة الوطنية لتوثيق شهداء ومفقودي الثورة السورية المباركة
            </h1>
            <p className="text-xs text-primary-200 mt-0.5">متابعة وتحديث الطلب</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Back */}
        <button
          onClick={() => navigate('/')}
          className="text-sm text-primary-600 hover:text-primary-800 mb-6 flex items-center gap-1"
        >
          <ChevronRight size={16} />
          العودة للصفحة الرئيسية
        </button>

        {/* Lookup form */}
        <div className="card mb-6">
          <h2 className="text-lg font-bold text-primary-700 mb-1">متابعة حالة الطلب</h2>
          <p className="text-stone-500 text-sm mb-5">
            أدخل الرقم المرجعي ورقم الهاتف الذي استخدمته عند تقديم الطلب
          </p>

          <form onSubmit={handleLookup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                الرقم المرجعي
              </label>
              <input
                type="text"
                value={refNumber}
                onChange={(e) => setRefNumber(e.target.value)}
                placeholder="مثال: DM-2026-000001"
                className="input-base w-full font-mono uppercase"
                dir="ltr"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                رقم الهاتف الأساسي
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+963XXXXXXXXX"
                className="input-base w-full"
                dir="ltr"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <Button variant="primary" type="submit" loading={loading} className="w-full flex items-center justify-center gap-1.5">
              <Search size={15} />
              البحث عن الطلب
            </Button>
          </form>
        </div>

        {/* Case info */}
        {caseInfo && (
          <div className="space-y-4">
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs text-stone-400 mb-0.5">الرقم المرجعي</p>
                  <p className="text-xl font-bold font-mono text-primary-800">{caseInfo.readable_id}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_BADGE[caseInfo.status] || 'bg-stone-100 text-stone-600'}`}>
                  {STATUS_LABELS[caseInfo.status] || caseInfo.status}
                </span>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className="text-stone-400">اسم الشهيد / المفقود</dt>
                <dd className="font-medium">{caseInfo.martyr_fullname || '—'}</dd>
                <dt className="text-stone-400">المنطقة</dt>
                <dd className="font-medium">{caseInfo.region || '—'}</dd>
                <dt className="text-stone-400">تاريخ التقديم</dt>
                <dd className="font-medium">
                  {caseInfo.submitted_at ? new Date(caseInfo.submitted_at).toLocaleDateString('ar-SY') : '—'}
                </dd>
              </dl>

              {caseInfo.submitter_notes && (
                <div className="mt-4 p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-400 mb-1">ملاحظاتك السابقة</p>
                  <p className="text-sm text-stone-700">{caseInfo.submitter_notes}</p>
                </div>
              )}
            </div>

            {/* Edit form (only if editable_fields configured) */}
            {caseInfo.editable_fields?.length > 0 && (
              <div className="card">
                <h3 className="font-semibold text-primary-700 mb-1">تحديث معلومات الطلب</h3>
                <p className="text-xs text-stone-400 mb-4">
                  يمكنك تعديل الحقول التالية وإرسالها للمراجعة
                </p>

                {updateSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle size={15} className="flex-shrink-0" />
                    تم تحديث معلومات الطلب بنجاح
                  </div>
                )}

                <form onSubmit={handleUpdate} className="space-y-4">
                  {caseInfo.editable_fields.map((field) => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        {FIELD_LABELS[field] || field}
                      </label>
                      {field === 'submitter_notes' ? (
                        <textarea
                          value={editValues[field] || ''}
                          onChange={(e) => setEditValues((prev) => ({ ...prev, [field]: e.target.value }))}
                          rows={3}
                          className="input-base w-full resize-none"
                          placeholder="أضف ملاحظات أو معلومات إضافية..."
                        />
                      ) : (
                        <input
                          type="text"
                          value={editValues[field] || ''}
                          onChange={(e) => setEditValues((prev) => ({ ...prev, [field]: e.target.value }))}
                          className="input-base w-full"
                          dir="ltr"
                        />
                      )}
                    </div>
                  ))}

                  <Button
                    variant="primary"
                    type="submit"
                    loading={updateLoading}
                  >
                    حفظ التغييرات
                  </Button>
                </form>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  )
}
